import { useState } from "react";
import { UseFormSetValue, UseFormWatch } from "react-hook-form";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import { IMaterialsResponse } from "@miplatform/types";
import {
  getSelectedAttributeValues,
  simpleSort,
  sortCategoryAttributes,
} from "@miplatform/utils";
import { useCategories, useMaterials } from "@mibuildweb/hooks";
import { IItemCategory, IItemData, ILinkedMaterial } from "../../Items.types";

export const useItemLinkItemTabViewModel = ({
  watch,
  setValue,
}: IUseItemLinkItemTabViewModelProps) => {
  const [filteredMaterials, setFilteredMaterials] = useState<
    IMaterialsResponse[]
  >([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] =
    useState<IItemCategory | null>(null);
  const [selectedAttributeValues, setSelectedAttributeValues] = useState({});
  const [openMaterialResults, setOpenMaterialResults] = useState(false);

  const debouncedSearch = useDebounce(search, 300);
  const { linkedMaterials, id: itemId } = watch();

  const {
    getCategoryFilters,
    categoryFilterOptions,
    setCategoryFilterOptions,
  } = useCategories();

  const {
    isLoading: isMaterialsLoading,
    getMaterials,
    setMaterials,
    materials,
  } = useMaterials();

  const fetchCategoryFilters = async () => {
    const response = await getCategoryFilters();
    if (response.status === HTTP_STATUSES.OK) {
      setCategoryFilterOptions(response.data);
    }
  };

  const fetchMaterials = async () => {
    const response = await getMaterials({
      filters: { not: { id: { type: "guid", value: itemId } } }, // Exclude current item from materials list to prevent circular reference
    });
    if (response.status === HTTP_STATUSES.OK) {
      setMaterials(response.data);
    }
  };

  const handleAttributeValueClick = (
    attributeId: string,
    attributeValue: string
  ) => {
    getSelectedAttributeValues(
      attributeId,
      attributeValue,
      selectedAttributeValues,
      setSelectedAttributeValues
    );
    setOpenMaterialResults(true);
  };

  const handleCategoryClick = (category: IItemCategory) => {
    const sortedCategory = sortCategoryAttributes(category);
    setSelectedCategory(sortedCategory);
    setOpenMaterialResults(true);
  };

  const handleReset = () => {
    setSelectedCategory(null);
    setSelectedAttributeValues({});
    setSearch("");
    setOpenMaterialResults(false);
  };

  const handleResetResultContainer = () => {
    setSearch("");
    setSelectedAttributeValues({});
    setSelectedCategory(null);
    setFilteredMaterials([]);
    setOpenMaterialResults(false);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setOpenMaterialResults(true);
  };

  const handleRemoveItem = (item: ILinkedMaterial) => {
    const updatedData = linkedMaterials.filter((d) => d.id !== item.id);
    if (updatedData.length > 0) {
      setValue("linkedMaterials", updatedData, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else {
      setValue("linkedMaterials", null as any, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue("linkedMaterials", [], {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const handleAddItem = (item: ILinkedMaterial) => {
    const updatedData = [
      ...linkedMaterials,
      { ...item, materialId: item.id, quantity: 1 },
    ];
    const sortedData = updatedData.sort((a: any, b: any) =>
      simpleSort(a.category?.itemCode, b.category?.itemCode)
    );
    setValue("linkedMaterials", sortedData, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const hasFilters = !!selectedCategory?.categoryId || !!debouncedSearch;

  return {
    isLoading: isMaterialsLoading,
    fetchMaterials,
    materials,
    fetchCategoryFilters,
    categoryFilterOptions,
    search,
    setSearch,
    selectedCategory,
    handleCategoryClick,
    handleAttributeValueClick,
    selectedAttributeValues,
    handleReset,
    setFilteredMaterials,
    filteredMaterials,
    handleResetResultContainer,
    debouncedSearch,
    openMaterialResults,
    setOpenMaterialResults,
    hasFilters,
    handleSearch,
    handleRemoveItem,
    handleAddItem,
  };
};

interface IUseItemLinkItemTabViewModelProps {
  watch: UseFormWatch<IItemData>;
  setValue: UseFormSetValue<IItemData>;
}
